import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { meMoneyT, type MeMoneyLocale } from './meMoneyLocales';
import type { MeMoneyMmMoneyBrandingHandlers, MeMoneyToastFn } from './meMoneyTypes';
import { MmMoneyBrandingSampleTokenCard } from './MmMoneyBrandingSampleTokenCard';

/** Accepts empty (clear), http(s) URLs, site-relative paths, and data:image/* (no HTTPS-only rule). */
function isAllowedMmMoneyImageRef(raw: string): boolean {
  const u = raw.trim();
  if (!u) return true;
  if (u.startsWith('data:image/')) return true;
  if (u.startsWith('/') && !u.startsWith('//')) return true;
  if (u.startsWith('./') || u.startsWith('../')) return true;
  try {
    const parsed = new URL(u);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/** MM$ icon thumbnail preview beside URL field. */
function MmMoneyIconPreview(props: {
  src: string;
  brokenLabel: string;
  caption: string;
}) {
  const { src, brokenLabel, caption } = props;
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [src]);

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{caption}</p>
      <div className="flex items-center gap-3">
        <div
          role="img"
          aria-label={caption}
          className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-muted"
        >
          {broken ? (
            <div className="flex h-full items-center justify-center px-2 text-center text-[10px] leading-tight text-muted-foreground">
              {brokenLabel}
            </div>
          ) : (
            <img
              src={src.trim()}
              alt=""
              className="h-full w-full object-cover"
              decoding="async"
              onError={() => setBroken(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export interface MmMoneyAppearanceCardProps {
  toast: MeMoneyToastFn;
  locale?: string;
  mmMoneyBranding: MeMoneyMmMoneyBrandingHandlers;
  /** Hosted upload → public URL (`POST /api/v1/currency/branding/upload`). Second arg distinguishes icon vs backdrop. */
  uploadImage?: (file: File, kind: 'icon' | 'background') => Promise<string>;
  /** When set, sample TokenCard preview uses realistic `MM$<issuer>` for signed-in issuer. */
  previewIssuerUserId?: string;
}

/**
 * Standalone MM$ card background + icon editor (URLs, paths, data URLs, or optional hosted upload via `uploadImage`).
 * Used from user Profile and from MeMoneyScreen.
 */
export function MmMoneyAppearanceCard({
  toast,
  locale,
  mmMoneyBranding,
  uploadImage,
  previewIssuerUserId,
}: MmMoneyAppearanceCardProps) {
  const tx = useCallback(
    (key: string, params?: Record<string, string | number>) => meMoneyT(locale as MeMoneyLocale, key, params),
    [locale]
  );

  const lastPersistedRef = useRef({ bg: '', icon: '' });

  const [mmBgUrl, setMmBgUrl] = useState('');
  const [mmIconUrl, setMmIconUrl] = useState('');
  const [syncingManual, setSyncingManual] = useState(false);
  const [uploadingField, setUploadingField] = useState<null | 'bg' | 'icon'>(null);
  const bgFileRef = useRef<HTMLInputElement>(null);
  const iconFileRef = useRef<HTMLInputElement>(null);

  /** Load from platform and normalize fingerprint for autosave-after-paste detection (runs after uploads too). */
  const reloadBrandingIntoForm = useCallback(async (): Promise<boolean> => {
    try {
      const b = await mmMoneyBranding.load();
      const bgTrim = (b.backgroundImageUrl ?? '').trim();
      const iconTrim = (b.currencyIconUrl ?? '').trim();
      setMmBgUrl(bgTrim);
      setMmIconUrl(iconTrim);
      lastPersistedRef.current = { bg: bgTrim, icon: iconTrim };
      return true;
    } catch {
      return false;
    }
  }, [mmMoneyBranding]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await reloadBrandingIntoForm();
      if (!ok && !cancelled) {
        toast({
          title: tx('mmMoneyLoadFailedTitle'),
          description: tx('mmMoneyLoadFailedDesc'),
          variant: 'destructive',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadBrandingIntoForm, toast, tx]);

  /** Pasted/manual URLs — PATCH mirrors where upload API is not involved. Silent on success. */
  const persistManualIfDirty = useCallback(async () => {
    const bg = mmBgUrl.trim();
    const icon = mmIconUrl.trim();
    const prev = lastPersistedRef.current;
    if (bg === prev.bg && icon === prev.icon) return;

    if (!isAllowedMmMoneyImageRef(bg) || !isAllowedMmMoneyImageRef(icon)) {
      toast({
        title: tx('mmMoneyInvalidTitle'),
        description: tx('mmMoneyInvalidUrl'),
        variant: 'destructive',
      });
      return;
    }

    setSyncingManual(true);
    try {
      await mmMoneyBranding.save({
        backgroundImageUrl: bg || undefined,
        currencyIconUrl: icon || undefined,
      });
      lastPersistedRef.current = { bg, icon };
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Save failed';
      toast({ title: tx('toastCreateFailedTitle'), description: message, variant: 'destructive' });
    } finally {
      setSyncingManual(false);
    }
  }, [mmBgUrl, mmIconUrl, mmMoneyBranding, toast, tx]);

  const onFileChosen = async (which: 'bg' | 'icon', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !uploadImage) return;
    if (!file.type.startsWith('image/')) {
      toast({
        title: tx('mmMoneyUploadInvalidTypeTitle'),
        description: tx('mmMoneyUploadInvalidTypeDesc'),
        variant: 'destructive',
      });
      return;
    }
    setUploadingField(which);
    try {
      await uploadImage(file, which === 'bg' ? 'background' : 'icon');
      const ok = await reloadBrandingIntoForm();
      toast({
        title: tx('mmMoneyUploadOkTitle'),
        description: ok ? tx('mmMoneyUploadOkDesc') : tx('mmMoneyUploadReloadSkippedDesc'),
        variant: 'default',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : tx('mmMoneyUploadFailedDesc');
      toast({
        title: tx('mmMoneyUploadFailedTitle'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setUploadingField(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{tx('tabMmMoney')}</CardTitle>
        <CardDescription>{tx('mmMoneyIntro')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>{tx('labelMmBg')}</Label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              className="min-w-0 flex-1"
              placeholder={tx('placeholderHttps')}
              value={mmBgUrl}
              onChange={(e) => setMmBgUrl(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={Boolean(syncingManual || uploadingField)}
              onBlur={() => void persistManualIfDirty()}
            />
            {uploadImage ? (
              <>
                <input
                  ref={bgFileRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  aria-hidden
                  tabIndex={-1}
                  onChange={(e) => onFileChosen('bg', e)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={Boolean(syncingManual || uploadingField)}
                  onClick={() => bgFileRef.current?.click()}
                  aria-label={tx('mmMoneyUploadAriaBg')}
                >
                  {uploadingField === 'bg' ? tx('mmMoneyUploading') : tx('mmMoneyUpload')}
                </Button>
              </>
            ) : null}
          </div>
          {mmBgUrl.trim() && isAllowedMmMoneyImageRef(mmBgUrl) ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">{tx('mmMoneyPreviewTokenCard')}</p>
              <MmMoneyBrandingSampleTokenCard
                issuerUserId={previewIssuerUserId}
                backgroundUrl={mmBgUrl.trim()}
                currencyIconUrl={mmIconUrl.trim() ? mmIconUrl.trim() : undefined}
              />
            </div>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label>{tx('labelMmIcon')}</Label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              className="min-w-0 flex-1"
              placeholder={tx('placeholderHttps')}
              value={mmIconUrl}
              onChange={(e) => setMmIconUrl(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={Boolean(syncingManual || uploadingField)}
              onBlur={() => void persistManualIfDirty()}
            />
            {uploadImage ? (
              <>
                <input
                  ref={iconFileRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  aria-hidden
                  tabIndex={-1}
                  onChange={(e) => onFileChosen('icon', e)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={Boolean(syncingManual || uploadingField)}
                  onClick={() => iconFileRef.current?.click()}
                  aria-label={tx('mmMoneyUploadAriaIcon')}
                >
                  {uploadingField === 'icon' ? tx('mmMoneyUploading') : tx('mmMoneyUpload')}
                </Button>
              </>
            ) : null}
          </div>
          {mmIconUrl.trim() && isAllowedMmMoneyImageRef(mmIconUrl) ? (
            <MmMoneyIconPreview
              src={mmIconUrl}
              caption={tx('mmMoneyPreviewIcon')}
              brokenLabel={tx('mmMoneyPreviewUnloadable')}
            />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
